// Counterparty quote overlay for the Swap Curve (LQC-1899). Pure logic - no JSX, keep as .ts.
//
// Each quoting CP's price for a swap block is read from that CP's own quote legs. Every CP quotes
// the SAME swap under a DIFFERENT blockId, so legs are matched by the shared `leg` ordinal rather
// than by blockId - the rule the indicative mid already uses (LQC-1898).

import { ExecutionRequestStatus } from "@/lib/interfaces and Enums/ENUMExecutionRequestStatus";
import { QuoteSide } from "@/lib/interfaces and Enums/ENUMQuoteSide";
import { IExecutionRequestCounterParties } from "@/lib/interfaces and Enums/InterfaceExecutionRequestCounterParties";
import { ExecutionRequestQuoteLeg } from "@/lib/interfaces and Enums/InterfaceExecutionRequestQuoteLeg";
import { ExecutionStrategy } from "@/lib/interfaces and Enums/InterfaceExecutionStrategy";
import {
  getTradeLegs,
  legSecurityName,
  parseIndicativeMid,
  parseLegMaturityDate,
  yAxisForNotation,
} from "./swapCurveHelpers";

// One CP's quote for one swap block, ready to plot on the same axes as the curve.
export interface CounterpartyQuoteMarker {
  cpName: string;
  executionRequestId?: number;
  order: number; // back-end ranking; 1 = best, 9999 = unranked
  // Position among the trade's in-competition CPs, NOT among those that have quoted. This is what
  // the chart colours and shapes by, so a CP keeps its identity when another one quotes later.
  cpIndex: number;
  status?: ExecutionRequestStatus;
  blockId: number; // the structure block the curve was fetched for (tooltip fallback only)
  securityName?: string; // what the tooltip shows; blockId is only the fallback
  leg: string | number; // the ordinal that tied the structure block to this CP's quote leg
  quote: number; // in the leg's own unit - see `notation`
  notation?: string; // "%" plots on Zero Rate, "bps" on the Spread axis (LQC-2883)
  quoteSide?: QuoteSide; // which side the value came from, for the tooltip
  tenorDate?: Date; // the quote leg's maturity; undefined -> chart falls back to earliest tenor
}

// Statuses that mean a CP is out of the competition, so their quote is stale and not plotted.
// Duplicated from colDefsShared's `eliminatedStatuses` on purpose: that module sits inside the
// Action Summary import cycle and must not be pulled into this widget (see CLAUDE.md).
export const CP_ELIMINATED_STATUSES: ExecutionRequestStatus[] = [
  ExecutionRequestStatus.Recalled,
  ExecutionRequestStatus.NotDealt,
  ExecutionRequestStatus.DealtAway,
  ExecutionRequestStatus.Rejected,
  ExecutionRequestStatus.Pulled,
  ExecutionRequestStatus.Closed,
  ExecutionRequestStatus.Archived,
  ExecutionRequestStatus.OffQuote,
  ExecutionRequestStatus.InvalidQuote,
  ExecutionRequestStatus.QuotedExpired,
  ExecutionRequestStatus.QuoteExpired,
];

// Statuses that mean this CP won the trade - they are "best" regardless of `order`. Mirrors the
// awarded/executed branch of getBestCounterparty (same cycle reason as above).
export const CP_WON_STATUSES: ExecutionRequestStatus[] = [
  ExecutionRequestStatus.Awarded,
  ExecutionRequestStatus.Executed,
  ExecutionRequestStatus.ExecutedPend,
  ExecutionRequestStatus.ExecutedRPChangeRequested,
  ExecutionRequestStatus.ExecutedPendRPChangeRequested,
  ExecutionRequestStatus.ExecutedRPAgreeChange,
  ExecutionRequestStatus.ExecutedPendRPAgreeChange,
  ExecutionRequestStatus.ExecutedOPChangeRequested,
  ExecutionRequestStatus.ExecutedPendOPChangeRequested,
  ExecutionRequestStatus.Affirmed,
  ExecutionRequestStatus.AffirmedPend,
  ExecutionRequestStatus.Filled,
  ExecutionRequestStatus.NoMoreFills,
];

// A CP quote as a plain number. Wraps parseIndicativeMid and additionally rejects 0, which the
// back end uses as "no quote on this side" (a 0.000% swap rate is never a real quote).
export const parseCounterpartyQuote = (
  value?: number | string | null,
): number | undefined => {
  const parsed = parseIndicativeMid(value);
  return parsed === undefined || parsed === 0 ? undefined : parsed;
};

// The bid/ask/mid fields to try, in order, for a leg quoted on the given side. Two-Way/Both/Mid
// and an absent side all fall back to mid first, then either outright side.
export const quoteFieldPreference = (
  side?: QuoteSide,
): ("bid" | "ask" | "mid")[] => {
  if (side === QuoteSide.Bid) return ["bid", "mid", "ask"];
  if (side === QuoteSide.Ask) return ["ask", "mid", "bid"];
  return ["mid", "bid", "ask"];
};

// The value this CP quoted on a leg, plus which side it came from. Undefined when the leg is
// missing, quoted in a unit no axis carries (neither % nor bps), or has no numeric value.
export const resolveLegQuote = (
  leg?: ExecutionRequestQuoteLeg | null,
): { value: number; side: QuoteSide } | undefined => {
  if (!leg || !yAxisForNotation(leg.notation)) return undefined;

  const sideForField: Record<string, QuoteSide> = {
    bid: QuoteSide.Bid,
    ask: QuoteSide.Ask,
    mid: QuoteSide.Mid,
  };

  for (const field of quoteFieldPreference(leg.quoteSide)) {
    const value = parseCounterpartyQuote(leg[field]);
    if (value !== undefined) return { value, side: sideForField[field] };
  }
  return undefined;
};

// Display label for a CP - the provider code shown everywhere else in the app, with fallbacks.
export const counterpartyDisplayName = (
  cp: IExecutionRequestCounterParties,
  index: number,
): string => cp.provider || cp.name || `CP ${index + 1}`;

// True when the CP is still in the competition, so their quote is live rather than stale.
export const counterpartyIsInCompetition = (
  cp: IExecutionRequestCounterParties,
): boolean =>
  !cp.status ||
  !CP_ELIMINATED_STATUSES.includes(cp.status as ExecutionRequestStatus);

// The distinct swap "slots" to look up on every CP: one `leg` ordinal per swap block, with a
// representative block id for display. Deduped BY ORDINAL - when a trade has no top-level legs
// the swap block ids span every CP, and keying on blockId would repeat each slot once per CP.
export const swapLegSlots = (
  structureLegs: ExecutionRequestQuoteLeg[],
  swapBlockIds: number[],
): { leg: string | number; blockId: number }[] => {
  const slots: { leg: string | number; blockId: number }[] = [];
  const seen = new Set<string>();

  swapBlockIds.forEach((blockId) => {
    const leg = structureLegs.find((l) => l.blockId === blockId)?.leg;
    if (leg === undefined || leg === null) return;
    const key = String(leg);
    if (seen.has(key)) return;
    seen.add(key);
    slots.push({ leg, blockId });
  });
  return slots;
};

// Every live CP quote for the given swap blocks, one marker per (CP, swap block). Empty for RP
// trades, block drops, and any trade where no CP has put a numeric rate on a swap leg.
export const extractCounterpartyQuotesFromTrade = (
  trade: ExecutionStrategy | null | undefined,
  swapBlockIds: number[],
): CounterpartyQuoteMarker[] => {
  const counterparties = trade?.executionRequestCounterParties;
  if (!counterparties || counterparties.length === 0) return [];

  const slots = swapLegSlots(getTradeLegs(trade), swapBlockIds);
  if (slots.length === 0) return [];

  const markers: CounterpartyQuoteMarker[] = [];
  // Counted over every CP still in the competition, INCLUDING those yet to quote - a CP that
  // quotes later must not renumber the ones already on the chart.
  let cpIndex = -1;
  counterparties.forEach((cp, index) => {
    if (!counterpartyIsInCompetition(cp)) return;
    cpIndex += 1;
    const cpName = counterpartyDisplayName(cp, index);

    slots.forEach((slot) => {
      // `leg` arrives as a number or a string depending on the payload, so compare as strings.
      const cpLeg = cp.legs?.find((l) => String(l.leg) === String(slot.leg));
      const quote = resolveLegQuote(cpLeg);
      if (!quote) return;

      markers.push({
        cpName,
        executionRequestId: cp.executionRequestId,
        order: cp.order ?? Number.MAX_SAFE_INTEGER,
        cpIndex,
        status: cp.status,
        blockId: slot.blockId,
        securityName: legSecurityName(cpLeg),
        leg: slot.leg,
        quote: quote.value,
        notation: cpLeg?.notation,
        quoteSide: quote.side,
        tenorDate: parseLegMaturityDate(cpLeg?.maturity),
      });
    });
  });
  return markers;
};

// The winning CP's name: whoever was awarded/executed, else the best back-end `order`. Undefined
// when nothing has been quoted. Mirrors getBestCounterparty without importing its module.
export const bestCounterpartyName = (
  markers: CounterpartyQuoteMarker[],
): string | undefined => {
  if (markers.length === 0) return undefined;
  return markers.reduce((best, m) =>
    counterpartyRankKey(m) < counterpartyRankKey(best) ? m : best,
  ).cpName;
};

// Lower ranks first: a won CP beats every back-end `order`.
export const counterpartyRankKey = (marker: CounterpartyQuoteMarker): number =>
  marker.status && CP_WON_STATUSES.includes(marker.status)
    ? Number.MIN_SAFE_INTEGER
    : marker.order;

// CP groups in quote-rank order (LQC-3190), so the legend matches the blotter's 1st/2nd/3rd.
// Stable, so equal ranks keep their CP-list order.
export const rankCounterpartyGroups = <
  T extends { quotes: CounterpartyQuoteMarker[] },
>(
  groups: T[],
): T[] => {
  const rankOf = (g: T) => Math.min(...g.quotes.map(counterpartyRankKey));
  return [...groups].sort((a, b) => rankOf(a) - rankOf(b));
};

// Markers grouped per CP, preserving the counterparty order they were extracted in. One group
// becomes one chart series (or one legend group, for the multi-series display modes). `cpIndex` is
// carried on the group because the caller colours by it - the group's own array position moves as
// counterparties quote, that index does not.
export const groupQuotesByCounterparty = (
  markers: CounterpartyQuoteMarker[],
): { cpName: string; cpIndex: number; quotes: CounterpartyQuoteMarker[] }[] => {
  const groups: {
    cpName: string;
    cpIndex: number;
    quotes: CounterpartyQuoteMarker[];
  }[] = [];

  markers.forEach((marker) => {
    const group = groups.find((g) => g.cpName === marker.cpName);
    if (group) group.quotes.push(marker);
    else
      groups.push({
        cpName: marker.cpName,
        cpIndex: marker.cpIndex,
        quotes: [marker],
      });
  });
  return groups;
};
